import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { AuditLogEntry, InboundDelivery } from "../shared/types";

export type SortOrder = "asc" | "desc";

const auditContext = createContext<{
    auditLogEntries: AuditLogEntry[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    sortField: string;
    sortOrder: SortOrder;
    fetchAuditLogEntries: () => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setSort: (field: string) => void;
}>({
    auditLogEntries: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    sortField: "created_at",
    sortOrder: "desc",
    fetchAuditLogEntries: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setSort: () => { },
});

function parseAuditLogEntry(d: any): AuditLogEntry {
    return {
        ...d,
        created_at: new Date(d.created_at)
    };
}

function useProviderAuditLogEntries() {
    const [auditLogEntries, setAuditLogEntries] = useState<AuditLogEntry[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchAuditLogEntries();
    }, [page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchAuditLogEntries() {
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(
                `/audit/log?${params.toString()}`
            );
            if (!response.ok) {
                setErrors([
                    'Loading audit log entries failed. Please try again.',
                ]);
                return;
            }
            const json = await response.json();
            const data: AuditLogEntry[] = (json.data ?? json).map(parseAuditLogEntry);
            setTotal(json.meta.total ?? data.length);
            setAuditLogEntries(data);
        } catch (error) {
            console.error(error);
            setErrors([
                'Loading audit log entries failed. Please try again.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    return {
        auditLogEntries,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        sortField,
        sortOrder,
        fetchAuditLogEntries,
        setPage,
        setPerPage,
        setSearchTerm,
        setSort,
    }
}

export function ProviderAuditLogEntries({ children }: { children: React.ReactNode }) {
    const auditLogEntries = useProviderAuditLogEntries();
    return <auditContext.Provider value={auditLogEntries}> {children}</auditContext.Provider>;
}

export const useAuditLogEntries = () => {
    return useContext(auditContext);
}

export function useAuditLogEntryDetail(id: string | undefined) {
    const [auditLogEntry, setAuditLogEntry] = useState<AuditLogEntry | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (id) fetchAuditLogEntry();
    }, [id]);

    async function fetchAuditLogEntry() {
        if (!id) return;
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/audit/log/${id}`);
            if (!response.ok) {
                setErrors(['Loading audit log entry failed. Please try again.']);
                return;
            }
            const json = await response.json();
            setAuditLogEntry(parseAuditLogEntry(json.data ?? json));
        } catch (error) {
            console.error(error);
            setErrors(['Loading audit log entry failed. Please try again.']);
        } finally {
            setLoading(false);
        }
    }

    return { auditLogEntry, errors, loading, fetchAuditLogEntry };
}